class ImportUI {
  constructor() {
    this.fieldTypes = {};
    this.fieldTargets = {};
    this.fieldsIncluded = {};
    this.showFieldsInPreview = true; // todo: make toggleable
    this.headerOption = true;
    this.showFieldInCardOption = true;
    this.importProgress = 0;
    this.abortImport = false;
  }

  setCanvas(canvas) {
    this.canvas = canvas;
  }

  reset() {
    this.fieldTypes = {};
    this.fieldTargets = {};
    this.fieldsIncluded = {};
    this.rows = [];
    this.fields = [];
    this.headerOption = true;
    this.showFieldInCardOption = true;
    this.importProgress = 0;
    this.abortImport = false;
  }

  isValidFileType(file) {
    if (!file) return false;
    if (file.type.match(/text/)) return true;
    return false;
  }

  setRows(rows) {
    this.rows = [];
    for (const row of rows) {
      const cleanRow = {};
      for (const field in row) {
        cleanRow[field.trim()] = row[field];
      }
      this.rows.push(cleanRow);
    }
  }

  setFields(fields) {
    this.fields = [];
    for (const field of fields) {
      this.fields.push(field.trim());
    }
  }

  setImportProgress(fraction) {
    this.importProgress = Math.round(fraction * 100);
    this.canvas.importModal.update();
  }

  triggerAbortImport() {
    this.abortImport = true;
  }

  // if there's no header row
  fillInDefaultFields() {
    let longestRow = this.rows[0];
    for (const row of this.rows) {
      if (row.length > longestRow) {
        longestRow = row;
      }
    }
    this.fields = [];
    let firstField;
    for (const col in longestRow) {
      this.fields.push(col);
      if (!firstField) {
        this.fieldTypes[col] = 'card';
        firstField = col;
      } else {
        this.fieldTypes[col] = this.getSuggestedFieldType(col);
        importUI.fieldTargets[col] = firstField;
      }
      this.fieldsIncluded[col] = true;
    }
  }

  getSuggestedFieldType(field) {
    if (!this.rows || !this.rows.length) return 'part';

    const colData = this.rows.map(row => row[field]);
    const digits = colData.filter(v => v.match(/^[\d\.\,]+$/));
    if (digits.length) return 'attribute';
    // if (maxLength < 250) return 'attribute';
    // if (!Object.values(this.fieldTypes).includes('summary')) return 'summary'; //  just one field can become a summary
    return 'part';
  }

  setFieldTarget(field,target) {
    this.fieldTargets[field] = target;
  }

  setFieldType(field,type) {
    this.fieldTypes[field] = type;
    if (type === 'card') {
      delete this.fieldTargets[field];
    }
  }

  getPreviewRow() {
    return this.rows[0];
  }

  getCardTypes() {
    return this.getFieldsOfType('card');
  }

  getCardOrPartFields() {
    const typeFields = [];
    for (const field in this.fieldTypes) {
      if (!this.fieldsIncluded[field]) continue;
      if (this.fieldTypes[field] === 'card' || this.fieldTypes[field] === 'part') {
        typeFields.push(field);
      }
    }
    return typeFields;
  }

  getFieldsOfType(type) {
    const typeFields = [];
    for (const field in this.fieldTypes) {
      if (!this.fieldsIncluded[field]) continue;
      if (this.fieldTypes[field] === type) {
        typeFields.push(field);
      }
    }
    return typeFields;
  }

  getFieldsOfTypeOnTarget(type,target) {
    const fields = this.getFieldsOfType(type);
    return fields.filter(f => this.fieldsIncluded[f] && this.fieldTargets[f] === target);
  }

  getSummaryOfField(field) {
    const fields = this.getFieldsOfTypeOnTarget('summary',field);
    return fields[0];
  }

  getAttributesOfField(field) {
    return this.getFieldsOfTypeOnTarget('attribute',field);
  }

  getPartsOfField(field) {
    return this.getFieldsOfTypeOnTarget('part',field);
  }

  getTopLevelCards() {
    const topLevelFields = this.getFieldsOfType('card');
    return this.getCards(topLevelFields);
  }

  /* turn a list of fields into a list of preview card objects for rendering */
  getCards(fields) {
    return fields.map(field => this.getPreviewCardData(field));
  }

  /* get all the data needed to render a preview card */
  getPreviewCardData(field) {
    const row = this.getPreviewRow();

    const summaryField = this.getSummaryOfField(field);
    const attributeFields = this.getAttributesOfField(field);
    const partFields = this.getPartsOfField(field);
    let attributes;
    if (attributeFields) {
      attributes = attributeFields.map(f => {
        return {
          key: this.headerOption ? f : '#',
          value: row[f]
        };
      });
    }
    if (this.showFieldInCardOption) {
      return {
        titleField: field,
        title: `${field}: ${row[field]}`,
        summaryField,
        summary: summaryField ? `${summaryField} ${row[summaryField]}` : null,
        attributes,
        partFields
      };

    }

    return {
      titleField: field,
      title: row[field],
      summaryField,
      summary: summaryField ? row[summaryField] : null,
      attributes,
      partFields
    };
  }

  getAvailableSummaryTargetsForField(field) {
    return this.getCardOrPartFields().filter(f => f !== field && !this.getSummaryOfField(f) || this.getSummaryOfField(f) === field);
  }

  getAvailablePartTargetsForField(field) {
    return this.getCardOrPartFields().filter(f => !this.isDescendantOf(field,f) );
  }

  getAvailableAttributeTargetsForField(field) {
    return this.getCardOrPartFields();
  }

  /*
    return true if target is a descendant of field
  */
  isDescendantOf(field,descendant) {
    if (field === descendant) return true;
    // get all parts with field as a target
    const partFields = this.getFieldsOfTypeOnTarget('part',field);
    for (const pfield of partFields) {
      if (this.isDescendantOf(pfield,descendant)) return true;
    }
    return false;
  }

  /* get all content nested under the given field */
  getAllDescendants(field,depth=0) {
    const partFields = this.getFieldsOfTypeOnTarget('part',field);
    let descendants = [];
    descendants = descendants.concat(partFields);
    for (const pfield of partFields) {
      const result = this.getAllDescendants(pfield,depth+1);
      descendants = descendants.concat(result.descendants);
      depth = Math.max(depth,result.depth);
    }
    let attributes = this.getFieldsOfTypeOnTarget('attribute',field);
    descendants = descendants.concat(attributes);

    let summaries = this.getFieldsOfTypeOnTarget('summary',field);
    descendants = descendants.concat(summaries);

    descendants.unshift(field);
    return {descendants,depth};
  }

  /*
    estimate the width of each column based on the content in each system
  */
  getColumnWidths() {
    const colWidths = {};
    const cardFields = this.getFieldsOfType('card');
    for (const row of this.rows) {

      for (const topField of cardFields) {
        if (!colWidths[topField]) {
          colWidths[topField] = 0;
        }

        const {descendants,depth} = this.getAllDescendants(topField);

        for (const field of descendants) {
          let cardEstWidth = 0;
          if (this.fieldTypes[field] === 'card' || this.fieldTypes[field] === 'part') {
            let title = row[field] || '';
            if (this.showFieldInCardOption) {
              title = `${field}: ${title}`;
            }
            const titleLineLength = Math.min(title.length,27);
            let titleElementEstLength = 64; // title element with no characters
            if (titleLineLength) {
              /* approx width per character times number of characters before title wraps */
              titleElementEstLength = 11 * titleLineLength;
            }
            cardEstWidth = 41 + titleElementEstLength;
          } else if (this.fieldTypes[field] === 'attribute') {
            const attr = row[field] || '';
            const attrLineLength = Math.min(attr.length + field.length,75);
            let attrElementEstLength = 75; // attr element with no characters
            if (attrLineLength) {
              /* approx width per character times number of characters before title wraps */
              attrElementEstLength = 8 * attrLineLength;
            }
            cardEstWidth = 60 + attrElementEstLength;
          } else if (this.fieldTypes[field] === 'summary') {
            let summary = row[field] || '';
            if (this.showFieldInCardOption) {
              summary = `${field}: ${summary}`;
            }
            const summaryLineLength = Math.min(summary.length,33);
            let summaryElementEstLength = 78; // summary element with no characters
            if (summaryLineLength) {
              /* approx width per character times number of characters before title wraps */
              summaryElementEstLength = 10 * summaryLineLength;
            }
            cardEstWidth = 30 + summaryElementEstLength;
          }
          if (depth) {
            cardEstWidth = cardEstWidth + (30 * depth);
          }
          cardEstWidth = Math.max(130,cardEstWidth);
          if (cardEstWidth > colWidths[topField]) {
            colWidths[topField] = cardEstWidth;
          }
        }
      }
    }
    return colWidths;
  }

  async csvToCards() {
    if (!this.fields.length || !this.rows.length) return;
    const {x,y} = appUI.viewportCoordsToMapCoords({x: 100, y: 150});
    const rows = this.rows;

    let nextX = x;
    let nextY = y;
    const allNodeIds = [];
    const cardFields = this.getFieldsOfType('card');
    const BUFFER_X = 50;
    const BUFFER_Y = 50;

    let rowHeight = 0;
    const colWidths = this.getColumnWidths();
    const columnCoords = {};
    let layout = 'columns';
    let cols = cardFields.length;
    let gridColCount = 0;
    if (cardFields.length === 1) {
      layout = 'grid';
      cols = Math.ceil(Math.sqrt(rows.length));
    }

    const totalCardsToRender = rows.length * cardFields.length;

    let progress = 0;
    const topLevelCardIds = [];
    let rowCardIds = [];
    for (const row of rows) {
      for (const field of cardFields) {
        if (this.abortImport) {
          this.abortImportedContent({ allNodeIds });
          return;
        }
        progress++;
        this.setImportProgress(progress/totalCardsToRender);
        await this.animationFrame();
        if (layout === 'columns') {
          if (!columnCoords[field]) {
            /* figure out region coords for this column */
            columnCoords[field] = {
              left: nextX,
              right: nextX + colWidths[field],
              top: nextY
            };
          }
        }
        if (!this.isCardEmpty({ row, field })) {
          const topLevelNodeId = this.addCard({
            row,
            field,
            x: nextX,
            y: nextY,
            allNodeIds
          });
          rowCardIds.push(topLevelNodeId);
          topLevelCardIds.push(topLevelNodeId);
        }

        if (progress === 1) {
          this.canvas.appFooterTag.update(); /* hide Start Mapping banner */
        }
        if (layout === 'columns') {
          nextX += colWidths[field] + BUFFER_X;
        } else if (layout === 'grid') {

          gridColCount++;
          nextX += colWidths[field] + BUFFER_X;
          if (gridColCount >= cols) {
            rowHeight = this.updateRow(rowCardIds);
            rowCardIds = [];
            nextY += rowHeight + BUFFER_Y;
            nextX = x;
            gridColCount = 0;
          }
        }
      }

      if (layout === 'columns') {
        rowHeight = this.updateRow(rowCardIds);
        rowCardIds = [];
        nextY += rowHeight + BUFFER_Y;
        nextX = x;
        for (const field in columnCoords) {
          columnCoords[field].bottom = nextY;
        }
      }
      rowHeight = 0;
    }
    const nodes = allNodeIds.map(id => this.canvas.store.getNode(id));
    const messages = [];

    const addBatchMessage = this.canvas.operation.messages.addBatch({ nodes, links: [] });
    messages.push(addBatchMessage);

    /* create a region to represent each spreadsheet column */
    if (layout === 'columns') {
      const regionIds = [];
      for (const field in columnCoords) {
        const coords = columnCoords[field];
        const region = regionUI.createRegion({
          top: coords.top - 80,
          left: coords.left - (BUFFER_X/2) + 2,
          right: coords.right + (BUFFER_X/2) - 2,
          bottom: coords.bottom + BUFFER_Y,
          title: field,
          operationMessages: messages
        });
        regionIds.push(region.id);
      }
      this.canvas.regionsTag.update();
      /* set all new elements to selected */
      regionUI.selectRegions(regionIds);
    }
    this.canvas.operation.batchOperation({
      messages,
      onFailure: err => {
        console.warn(`import csv batchOperation failed: ${err}`);
      }
    });

    this.canvas.nodesTag.update();
    const nodeTags = topLevelCardIds.map(id => this.canvas.getNodeTag({id})).filter(n => n !== undefined);
    /* set all new cards to selected */
    this.canvas.setSelectedElements(nodeTags,true);


    this.canvas.importModal.importDone();
  }

  updateRow(rowCardIds) {
    this.canvas.nodesTag.update();
    let rowHeight = 0;
    for (const topLevelNodeId of rowCardIds) {
      const nodeTag = this.canvas.getNodeTag({ id: topLevelNodeId });
      nodeTag.updateLayers();
      nodeTag.discoverPosition();
      if (nodeTag.instance.height > rowHeight) {
        rowHeight = nodeTag.instance.height;
      }
    }
    return rowHeight;
  }

  isCardEmpty({ row, field }) {
    const summaryField = this.getSummaryOfField(field);
    const attrFields = this.getFieldsOfTypeOnTarget('attribute',field);
    const partFields = this.getFieldsOfTypeOnTarget('part',field);

    const title = row[field];
    const summary = summaryField ? row[summaryField] : null;

    if (!title && !summary && attrFields.length === 0 && partFields.length === 0) return true;

    return false;
  }

  addCard({ row, field, parentId, x, y, allNodeIds }) {

    const summaryField = this.getSummaryOfField(field);
    let title = row[field];
    let summary = summaryField ? row[summaryField] : '';
    if (this.showFieldInCardOption) {
      title = `${field}: ${title}`;
      if (summary) {
        summary = `${summaryField}: ${summary}`;
      }
    }

    const nodeId = this.canvas.store.addNode({
      title,
      summary,
      parentId,
      x,
      y,
    });

    const attrFields = this.getFieldsOfTypeOnTarget('attribute',field);

    for (const attrField of attrFields) {
      const node = this.canvas.store.getNode(nodeId);
      const role = 'card';
      const name = this.headerOption ? attrField : '#';
      const value = row[attrField];
      if (!value) continue;
      if (node.getAttributeByValue({role, name, value})) continue;

      const attrId = node.addAttribute({ role, name, value });
    }

    allNodeIds.push(nodeId);

    const partFields = this.getFieldsOfTypeOnTarget('part',field);
    for (const partField of partFields) {
      if (!this.isCardEmpty({ row, field: partField })) {
        this.addCard({
          row,
          field: partField,
          parentId: nodeId,
          allNodeIds
        });
      }
    }
    return nodeId;
  }


  animationFrame() {
    let resolve = null;
    const promise = new Promise(r => resolve = r);
    window.requestAnimationFrame(resolve);
    return promise;
  }

  abortImportedContent({ allNodeIds }) {
    this.canvas.store.deleteNodes({ nodeIds: allNodeIds });
    this.abortImport = false;
    this.importProgress = 0;
    const { canvas } = this;
    canvas.nodesTag.update();
    canvas.importModal.hideProgress();
    canvas.importModal.update();
    canvas.clearSelectedElements();
    canvas.appFooterTag.update();
  }

}

const importUI = new ImportUI();
window.importUI = importUI;
export { ImportUI, importUI };



// WEBPACK FOOTER //
// ./starling/src/lib/import-ui.js